import { CalculatorOutlined, DollarOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import {
    Button,
    Col,
    InputNumber,
    Modal,
    Row,
    Space,
    Typography,
} from "antd";
import React, { useState } from "react";

const DenominacionesCalculator = ({
    value = 0,
    onChange,
    modalTitle = "Cálculo de Importe por Denominaciones",
    buttonTitle = "Calcular",
    disabled = false,
    placeholder = "Importe calculado",
    useDefaultValues = true, // Nueva prop para controlar si usar valores por defecto
}) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [modalConfirmVisible, setModalConfirmVisible] = useState(false);
    const [importePendiente, setImportePendiente] = useState(0);
    const [denominaciones, setDenominaciones] = useState({
        100: 0,
        50: 0,
        20: 0,
        10: 0,
        5: 0,
        2: 0,
        1: 0,
        0.5: 0,
        0.25: 0,
        "0.10": 0,
        0.05: 0,
        0.01: 0,
    });
    const [totalCalculado, setTotalCalculado] = useState(0);

    const actualizarDenominacion = (valor, cantidad) => {
        setDenominaciones((prev) => {
            const nuevasDenominaciones = { ...prev, [valor]: cantidad };
            const nuevoTotal = Object.entries(nuevasDenominaciones).reduce(
                (sum, [val, cant]) => sum + parseFloat(val) * cant,
                0
            );
            setTotalCalculado(nuevoTotal);
            return nuevasDenominaciones;
        });
    };

    const establecerValoresPredeterminados = () => {
        const denominacionesPredeterminadas = {
            100: 0,
            50: 1,    // 1 de 50
            20: 2,    // 2 de 20
            10: 0,
            5: 1,     // 1 de 5
            2: 0,
            1: 4,     // 4 de 1
            0.5: 1,   // 1 de 0.5
            0.25: 0,
            "0.10": 0,
            0.05: 0,
            0.01: 0,
        };

        setDenominaciones(denominacionesPredeterminadas);

        // Calcular el total predeterminado: 50 + (2×20) + 5 + (4×1) + 0.5 = 99.5
        const totalPredeterminado = Object.entries(denominacionesPredeterminadas).reduce(
            (sum, [val, cant]) => sum + parseFloat(val) * cant,
            0
        );
        setTotalCalculado(totalPredeterminado);
    };

    const limpiarDenominaciones = () => {
        const denominacionesVacias = {
            100: 0,
            50: 0,
            20: 0,
            10: 0,
            5: 0,
            2: 0,
            1: 0,
            0.5: 0,
            0.25: 0,
            "0.10": 0,
            0.05: 0,
            0.01: 0,
        };

        setDenominaciones(denominacionesVacias);
        setTotalCalculado(0);
    };

    const abrirModal = () => {
        // Decidir si usar valores por defecto o limpiar denominaciones
        if (useDefaultValues) {
            establecerValoresPredeterminados();
        } else {
            limpiarDenominaciones();
        }
        setModalVisible(true);
    };

    const confirmarDenominaciones = () => {
        // Redondear el valor a 2 decimales para evitar errores de precisión
        const importeRedondeado = Math.round(totalCalculado * 100) / 100;

        // Solo verificar si el importe es diferente de 99.5 cuando se usan valores por defecto
        if (useDefaultValues && importeRedondeado !== 99.5) {
            // Guardar el importe pendiente y mostrar modal de confirmación
            setImportePendiente(importeRedondeado);
            setModalConfirmVisible(true);
        } else {
            // Si no se usan valores por defecto o es exactamente 99.5, proceder directamente
            procesarConfirmacion(importeRedondeado);
        }
    };

    const procesarConfirmacion = (importeRedondeado) => {
        // Llamar al callback con el nuevo valor
        if (onChange) {
            onChange(importeRedondeado);
        }

        // Mostrar mensaje de confirmación
        window.messageApi?.success(
            `Importe establecido: $${importeRedondeado.toFixed(2)}`
        );

        setModalVisible(false);
    };

    const confirmarImporteDiferente = () => {
        // Procesar con el importe pendiente
        procesarConfirmacion(importePendiente);
        setModalConfirmVisible(false);
        setImportePendiente(0);
    };

    const cancelarImporteDiferente = () => {
        // Cerrar solo el modal de confirmación, mantener el principal abierto
        setModalConfirmVisible(false);
        setImportePendiente(0);
    };

    const cancelarModal = () => {
        setModalVisible(false);
        // Restaurar el valor anterior
        setTotalCalculado(value);
    };

    return (
        <>
            <Space.Compact style={{ width: "100%" }}>
                <InputNumber
                    style={{ width: "85%" }}
                    prefix={<DollarOutlined />}
                    placeholder={placeholder}
                    min={0}
                    step={0.01}
                    precision={2}
                    disabled={true}
                    value={value}
                />
                <Button
                    type="primary"
                    icon={<CalculatorOutlined />}
                    onClick={abrirModal}
                    disabled={disabled}
                    style={{ width: "15%" }}
                    title={buttonTitle}
                />
            </Space.Compact>

            <Modal
                title={modalTitle}
                open={modalVisible}
                onOk={confirmarDenominaciones}
                onCancel={cancelarModal}
                width={600}
                footer={[
                    <Button key="cancel" onClick={cancelarModal}>
                        Cancelar
                    </Button>,
                    <Button key="submit" type="primary" onClick={confirmarDenominaciones}>
                        Confirmar Importe: ${totalCalculado.toFixed(2)}
                    </Button>,
                ]}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        Ingrese la cantidad de billetes y monedas:
                    </Typography.Title>
                    <Button
                        type="default"
                        size="small"
                        onClick={establecerValoresPredeterminados}
                    >
                        Valores por defecto ($99.5)
                    </Button>
                </div>

                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Typography.Title
                            level={5}
                            style={{ textAlign: "center", marginBottom: 16 }}
                        >
                            Billetes
                        </Typography.Title>
                        {["100", "50", "20", "10", "5", "2", "1"].map((valor) => (
                            <Space.Compact
                                style={{ width: "100%", marginBottom: 8 }}
                                key={valor}
                            >
                                <Typography.Text
                                    style={{
                                        width: "60px",
                                        textAlign: "right",
                                        paddingRight: "8px",
                                    }}
                                >
                                    ${valor}:
                                </Typography.Text>
                                <InputNumber
                                    style={{ width: "100%" }}
                                    min={0}
                                    value={denominaciones[valor]}
                                    onChange={(value) =>
                                        actualizarDenominacion(valor, value || 0)
                                    }
                                    placeholder="Cantidad"
                                />
                            </Space.Compact>
                        ))}
                    </Col>
                    <Col span={12}>
                        <Typography.Title
                            level={5}
                            style={{ textAlign: "center", marginBottom: 16 }}
                        >
                            Monedas
                        </Typography.Title>
                        {["0.5", "0.25", "0.10", "0.05", "0.01"].map((valor) => (
                            <Space.Compact
                                style={{ width: "100%", marginBottom: 8 }}
                                key={valor}
                            >
                                <Typography.Text
                                    style={{
                                        width: "60px",
                                        textAlign: "right",
                                        paddingRight: "8px",
                                    }}
                                >
                                    ${valor}:
                                </Typography.Text>
                                <InputNumber
                                    style={{ width: "100%" }}
                                    min={0}
                                    value={denominaciones[valor]}
                                    onChange={(value) =>
                                        actualizarDenominacion(valor, value || 0)
                                    }
                                    placeholder="Cantidad"
                                />
                            </Space.Compact>
                        ))}
                    </Col>
                </Row>

                <Typography.Title
                    level={4}
                    style={{ marginTop: 24, textAlign: "right" }}
                >
                    Total: ${totalCalculado.toFixed(2)}
                </Typography.Title>
            </Modal>

            {/* Modal de confirmación para importes diferentes de 99.5 */}
            <Modal
                title="Confirmación de Importe"
                open={modalConfirmVisible}
                onOk={confirmarImporteDiferente}
                onCancel={cancelarImporteDiferente}
                width={500}
                okText="Aceptar"
                cancelText="Cancelar"
                icon={<ExclamationCircleOutlined />}
            >
                <div style={{ padding: '16px 0' }}>
                    <p>
                        <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                        El importe calculado es <strong>${importePendiente.toFixed(2)}</strong>
                    </p>
                    <p>Este importe <strong>no es $99.5</strong> como se esperaba.</p>
                    <p>¿Desea continuar con este importe?</p>
                </div>
            </Modal>
        </>
    );
};

export default DenominacionesCalculator; 